/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useMemo } from 'react';
import type { PropertyResponseType } from '@services';
import { BrokerCard } from './sidebar/BrokerCard';
import { LocationCard } from './sidebar/LocationCard';
import { OwnershipCard } from './sidebar/OwnershipCard';
import { DocumentationCard } from './sidebar/DocumentationCard';
import { usePropertyBrokerAssignment } from './hooks/usePropertyBrokerAssignment';
import { useAgency } from '@/pages/authenticated/agency-pages/AgenciesContext';
import { useAuth } from '@context/authentication';
import { getCurrentUserRole, isOperationalOrHigher } from '@/shared/utils/permissions';
import { isSoloBrokerAgency } from '@/shared/utils/agency-type';
import type { PropertyDrawerSection } from '../PropertyDrawer/drawerSections';

interface PropertyDetailSidebarProps {
  property: PropertyResponseType;
  agencyId: string;
  propertyId: string;
  onBrokersSaved: () => void;
  /** Opens the property editor on one section, from a card's pencil. */
  onEditSection: (section: PropertyDrawerSection) => void;
}

/**
 * Right-rail cards on the property detail page: who is responsible, who owns
 * it, what the registry says and where it is.
 *
 * The metrics card that used to close this column is gone — those figures now
 * open the screen, where they are read. Keeping both would have stated the
 * same five numbers twice.
 *
 * Broker assignment is omitted for solo-broker agencies, and assigning or
 * removing a broker is offered only to owner, admin and operational roles —
 * a broker sees the card read-only.
 *
 * @param props - The property, its identifiers and the reload callback.
 * @returns The rendered sidebar.
 */
export const PropertyDetailSidebar: React.FC<PropertyDetailSidebarProps> = ({
  property,
  agencyId,
  propertyId,
  onBrokersSaved,
  onEditSection,
}) => {
  const agency = useAgency((s) => s.agency);
  const { user } = useAuth();
  const showBrokerAssignment = !isSoloBrokerAgency(agency.type);
  const canManageBrokers = isOperationalOrHigher(
    getCurrentUserRole(agency, user?.profile?.email),
  );
  const brokers = property.assignment?.assigned_brokers ?? [];
  // Who captured the listing is named like everyone else on the card: by the
  // member's name, not a guess from the e-mail.
  const memberNames = useMemo(() => Object.fromEntries(
    (agency.members ?? []).map((m) => [m.email, `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim()]),
  ), [agency.members]);
  const assignment = usePropertyBrokerAssignment(agencyId, propertyId, brokers, onBrokersSaved);

  return (
    <div className="flex min-w-0 flex-col gap-[var(--space-4)]" data-testid="property-detail-sidebar">
      {/* Where the listing is comes first: it is the one fact a broker
          checks before anything else on this rail. */}
      <LocationCard property={property} />
      {showBrokerAssignment && (
        <BrokerCard
          brokers={brokers}
          memberOptions={canManageBrokers ? assignment.memberOptions : []}
          onAssign={canManageBrokers ? assignment.onAssign : undefined}
          onRemove={canManageBrokers ? assignment.onRemove : undefined}
          saving={assignment.saving}
          capturedBy={property.assignment?.captured_by}
          externalCaptor={property.assignment?.external_captor}
          externalBroker={property.assignment?.external_broker}
          primaryBroker={property.assignment?.primary_broker}
          memberNames={memberNames}
        />
      )}
      <OwnershipCard
        property={property}
        agencyId={agencyId}
        onEdit={() => onEditSection('ownership')}
      />
      <DocumentationCard
        property={property}
        agencyId={agencyId}
        propertyId={propertyId}
        onSaved={onBrokersSaved}
        onEdit={() => onEditSection('documentation')}
      />
    </div>
  );
};
